import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import LinkifyText from '../components/LinkifyText';
import SmartMentionsInput from '../components/SmartMentionsInput';
import {
  Calendar, Badge, Modal, Form, Input, InputNumber, DatePicker, Select, Button, message,
  Radio, Tooltip, Typography, Space, Switch, Divider, Tag, List, Card, Descriptions, Alert, Avatar
} from 'antd';
import {
  PlusOutlined, LeftOutlined, RightOutlined, ShareAltOutlined,
  GiftOutlined, CheckSquareOutlined, DeleteOutlined, MinusCircleOutlined,
  TeamOutlined, VideoCameraOutlined, CalendarOutlined, SyncOutlined, UserOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import isoWeek from 'dayjs/plugin/isoWeek';
import { UsersService } from '../api/services/users.service';
import { MeetingsService } from '../api/services/meetings.service';
import { CalendarService } from '../api/services/calendar.service';
import { TasksService } from '../api/services/tasks.service';
import { ProjectsService } from '../api/services/projects.service';
import { useAuth } from '../store/auth';
import 'dayjs/locale/ru';

dayjs.extend(weekday);
dayjs.extend(isoWeek);
dayjs.locale('ru');

const { Text } = Typography;

const HOURS = Array.from({ length: 16 }, (_, i) => i + 7); // 7:00 – 22:00

const EVENT_COLORS = {
  meeting: '#1677ff',
  videoconference: '#722ed1',
  event: '#52c41a',
  task_deadline: '#fa8c16',
  task_overdue: '#ff4d4f',
  birthday: '#eb2f96',
  regular_meeting: '#9254de',
};

const EVENT_TYPE_LABELS = {
  meeting: 'Совещание',
  videoconference: 'Видеоконференция',
  event: 'Событие',
  task_deadline: 'Срок выполнения задачи',
  task_overdue: 'Просроченные задачи',
  birthday: 'День рождения',
  regular_meeting: 'Регулярное совещание',
};

const statusOptions = [
  { value: 'new', label: 'Новая' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'review', label: 'На проверке' },
  { value: 'done', label: 'Готово' },
  { value: 'cancelled', label: 'Отменена' },
];

const priorityOptions = [
  { value: 'low', label: 'Низкий' },
  { value: 'medium', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
  { value: 'critical', label: 'Критический' },
];

const taskTypeOptions = [
  { value: 'land_legal', label: 'Земельно-правовые' },
  { value: 'permissive', label: 'Разрешительные' },
  { value: 'expert', label: 'Экспертные' },
  { value: 'design_engineering', label: 'Проектно-инженерные' },
  { value: 'financial_estimate', label: 'Финансово-сметные' },
  { value: 'control_supervision', label: 'Контрольно-надзорные' },
  { value: 'logistics', label: 'Логистические' },
  { value: 'coordination', label: 'Координационные' },
  { value: 'sales_marketing', label: 'Продажно-маркетинговые' },
  { value: 'cadastral_registration', label: 'Кадастрово-регистрационные' },
  { value: 'warranty', label: 'Гарантийные (постстроительные)' },
  { value: 'judicial_claim', label: 'Судебно-претензионные' },
  { value: 'mobilization', label: 'Мобилизационные (ЧС/форс-мажор)' },
  { value: 'acceptance_transfer', label: 'Приемо-передаточные (УК)' },
  { value: 'organizational_regime', label: 'Организационно-режимные' },
  { value: 'household_administrative', label: 'Хозяйственно-административные (АХО)' },
  { value: 'gr_tasks', label: 'GR-задачи (административное лоббирование)' },
  { value: 'commercial_negotiations', label: 'Коммерческие переговоры с партнёрами' },
];

// ── DAVx⁵: отдельный CalDAV-пароль (не пароль ТЕРРА-СУП) ───
function CaldavDavx5Block({ onPassword, newCaldavPassword }) {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    CalendarService.getCaldavMe().then((r) => {
      setSettings(r.data || r);
    }).catch(() => {});
  }, []);

  const issuePassword = () => {
    Modal.confirm({
      title: 'Выдать новый CalDAV-пароль?',
      content: 'Пароль нужен только для синхронизации телефона (DAVx⁵/CalDAV) и не совпадает с паролем ТЕРРА-СУП. Если пароль уже выдан, старый перестанет работать.',
      okText: 'Выдать пароль',
      cancelText: 'Отмена',
      onOk: async () => {
        setLoading(true);
        try {
          const res = await CalendarService.resetCaldavPassword();
          const data = res.data || res;
          if (onPassword) onPassword(data.caldav_password);
          message.success('Пароль выдан — используйте его сейчас, он больше не будет показан');
        } catch (e) {
          message.error('Не удалось выдать пароль');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const base = settings?.caldav_url || `${window.location.origin}/caldav/`;
  const username = settings?.username || 'ваш логин';

  return (
    <div>
      <div style={{ marginBottom: 8, fontSize: '0.9em' }}>
        Приложение <strong>DAVx⁵</strong> (F-Droid / Google Play) → «+» → «Войти по URL и учётным данным»:
      </div>
      <div style={{ marginBottom: 4 }}>
        <Text type="secondary">Base URL:</Text>{' '}
        <Text copyable style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{base}</Text>
      </div>
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary">Имя пользователя:</Text>{' '}
        <Text copyable style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{username}</Text>
      </div>
      {newCaldavPassword ? (
        <Alert
          type="warning"
          showIcon
          message="Это новый CalDAV-пароль — он показывается ТОЛЬКО сейчас"
          description={
            <div>
              <Text code copyable style={{ fontSize: 16 }}>{newCaldavPassword}</Text>
              <div style={{ marginTop: 6, fontSize: '0.85em' }}>
                Вставьте его в DAVx⁵ в поле «Пароль». После закрытия окна пароль не восстановить — выдайте новый.
              </div>
            </div>
          }
        />
      ) : (
        <Button type="primary" ghost loading={loading} onClick={issuePassword}>
          {settings?.password_set ? 'Сменить пароль CalDAV' : 'Выдать пароль CalDAV'}
        </Button>
      )}
      <div style={{ marginTop: 6, fontSize: '0.85em', color: 'gray' }}>
        Пароль CalDAV отдельный (не пароль ТЕРРА-СУП). После смены заново введите пароль в DAVx⁵.
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isAdmin = (currentUser?.role_level >= 5 || currentUser?.username === 'khaminov');
  const isSecretary = currentUser?.special_role === 'secretary';

  const [events, setEvents] = useState([]);
  const [viewMode, setViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(dayjs());

  // modals
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [meetingFormOpen, setMeetingFormOpen] = useState(false);
  const [videoFormOpen, setVideoFormOpen] = useState(false);
  const [permModalOpen, setPermModalOpen] = useState(false);
  const [deadlineModalOpen, setDeadlineModalOpen] = useState(false);
  const [birthdayModalOpen, setBirthdayModalOpen] = useState(false);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [caldavModalOpen, setCaldavModalOpen] = useState(false);
  const [newCaldavPassword, setNewCaldavPassword] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedEventParticipants, setSelectedEventParticipants] = useState([]);

  const [clickedDate, setClickedDate] = useState(null);
  const [deadlineTasks, setDeadlineTasks] = useState([]);
  const [allDeadlineTasks, setAllDeadlineTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [birthdayPeople, setBirthdayPeople] = useState([]);

  // task detail
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [previewTask, setPreviewTask] = useState(null);

  const [editMode, setEditMode] = useState(false);

  const [eventForm] = Form.useForm();
  const [meetingForm] = Form.useForm();
  const [videoForm] = Form.useForm();
  const [permForm] = Form.useForm();

  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState(null);
  const [myPermissions, setMyPermissions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allBirthdays, setAllBirthdays] = useState([]);

  // ─── data fetching ────────────────────────────────────────

  const fetchProjects = () => {
    ProjectsService.getProjects().then(r => setProjects(r.data)).catch(() => {});
  };

  const fetchBirthdays = () => {
    console.log('[Calendar] fetchBirthdays called');
    CalendarService.getBirthdays().then(r => {
      console.log('[Calendar] Birthdays received:', r.data.length);
      setAllBirthdays(r.data);
    }).catch(e => {
      console.error('[Calendar] Birthdays error:', e);
    });
  };

  const fetchCalendars = () => {
    console.log('[Calendar] fetchCalendars called');
    CalendarService.getAvailableCalendars().then(r => {
      console.log('[Calendar] Available calendars response:', r.data);
      console.log('[Calendar] Number of calendars:', r.data.length);
      console.log('[Calendar] First calendar:', r.data[0]);
      setAvailableCalendars(r.data);
      setSelectedCalendarId(prev => {
        const newId = !prev && r.data.length > 0 ? r.data[0].user_id : prev;
        console.log('[Calendar] setSelectedCalendarId:', { prev, newId, isFirstLoad: !prev });
        return newId;
      });
    }).catch(e => {
      console.error('[Calendar] fetchCalendars error:', e);
      console.error('[Calendar] fetchCalendars error response:', e.response?.data);
    });
  };

  const fetchEvents = useCallback(() => {
    console.log('[Calendar] fetchEvents called with selectedCalendarId:', selectedCalendarId);
    if (!selectedCalendarId) {
      console.log('[Calendar] fetchEvents skipped - no selectedCalendarId');
      return;
    }
    CalendarService.getEvents({ user_id: selectedCalendarId })
      .then(r => {
        console.log('[Calendar] fetchEvents success, events count:', r.data.length);
        console.log('[Calendar] fetchEvents sample events:', r.data.slice(0, 3));
        setEvents(r.data);
      })
      .catch(e => {
        console.error('[Calendar] fetchEvents error:', e);
        console.error('[Calendar] fetchEvents error response:', e.response?.data);
      });
  }, [selectedCalendarId]);

  const fetchPermissions = () => {
    CalendarService.getPermissions().then(r => setMyPermissions(r.data)).catch(() => {});
  };

  const fetchUsers = () => {
    UsersService.getUsers().then(r => setAllUsers(r.data)).catch(() => {});
  };

  const fetchTasks = () => {
    TasksService.getTasks({ limit: 500 }).then(r => {
      const tasks = r.data.filter(t => !['done', 'cancelled'].includes(t.status));
      setAllTasks(tasks);
    }).catch(() => {});
  };

  useEffect(() => {
    fetchCalendars();
    fetchUsers();
    fetchProjects();
    fetchBirthdays();
    fetchTasks();
  }, []);
  useEffect(() => { fetchEvents(); }, [selectedCalendarId]);

  useEffect(() => {
    if (selectedEvent && !selectedEvent._virtual) {
      CalendarService.getEventParticipants(selectedEvent.id)
        .then(r => setSelectedEventParticipants(r.data))
        .catch(() => setSelectedEventParticipants([]));
    } else {
      setSelectedEventParticipants([]);
    }
  }, [selectedEvent]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const eventId = urlParams.get('eventId');
    if (eventId && events.length > 0) {
      const ev = events.find(e => e.id === parseInt(eventId));
      if (ev && (!selectedEvent || selectedEvent.id !== ev.id)) {
        setSelectedEvent(ev);
        setEventDetailOpen(true);
      }
    }
  }, [events]);

  const handleCopyEventLink = () => {
    if (!selectedEvent) return;
    const url = `${window.location.origin}/calendar?eventId=${selectedEvent.id}`;
    navigator.clipboard.writeText(url);
    message.success('Ссылка на событие скопирована');
  };

  const handleEditEventClick = () => {
    setEditMode(true);
    const ev = selectedEvent;
    if (ev.event_type === 'event') {
      eventForm.setFieldsValue({
        title: ev.title,
        description: ev.description,
        start_time: dayjs(ev.start_time),
        end_time: dayjs(ev.end_time),
        duration: dayjs(ev.end_time).diff(dayjs(ev.start_time), 'minute'),
        location: ev.location,
        project_ids: ev.linked_projects?.map(p => p.id) || [],
        task_ids: ev.linked_tasks?.map(t => t.id) || [],
      });
      setEventFormOpen(true);
    } else if (ev.event_type === 'meeting') {
      meetingForm.setFieldsValue({
        title: ev.title,
        description: ev.description,
        start_time: dayjs(ev.start_time),
        end_time: dayjs(ev.end_time),
        duration: dayjs(ev.end_time).diff(dayjs(ev.start_time), 'minute'),
        location: ev.location,
        participant_ids: selectedEventParticipants.map(p => p.user_id),
        project_ids: ev.linked_projects?.map(p => p.id) || [],
        task_ids: ev.linked_tasks?.map(t => t.id) || [],
      });
      setMeetingFormOpen(true);
    } else if (ev.event_type === 'videoconference') {
      videoForm.setFieldsValue({
        title: ev.title,
        description: ev.description,
        start_time: dayjs(ev.start_time),
        end_time: dayjs(ev.end_time),
        duration: dayjs(ev.end_time).diff(dayjs(ev.start_time), 'minute'),
        participant_ids: selectedEventParticipants.map(p => p.user_id),
      });
      setVideoFormOpen(true);
    }
  };

  const handleUpdateEvent = async (values) => {
    try {
      await CalendarService.updateEvent(selectedEvent.id, {
        title: values.title,
        description: values.description || null,
        start_time: values.start_time.second(0).millisecond(0).toISOString(),
        end_time: values.end_time.second(0).millisecond(0).toISOString(),
        location: values.location || null,
        task_ids: values.task_ids || [],
        project_ids: values.project_ids || [],
      });
      message.success('Событие обновлено');
      setEventFormOpen(false);
      setMeetingFormOpen(false);
      setVideoFormOpen(false);
      setEditMode(false);
      fetchEvents();
      // Refresh selected event
      CalendarService.getEventById(selectedEvent.id).then(r => setSelectedEvent(r.data)).catch(()=>{});
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка обновления события');
    }
  };

  // ─── build virtual events for every visible day ───────────

  const visibleDays = useMemo(() => {
    console.log('[Calendar] visibleDays useMemo:', { viewMode, currentDate: currentDate.format('YYYY-MM-DD') });
    if (viewMode === 'day') return [currentDate];
    if (viewMode === 'week') {
      const start = currentDate.startOf('isoWeek');
      return Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));
    }
    const start = currentDate.startOf('month').startOf('isoWeek');
    const end = currentDate.endOf('month').endOf('isoWeek');
    const days = [];
    let d = start;
    while (d.isBefore(end) || d.isSame(end, 'day')) {
      days.push(d);
      d = d.add(1, 'day');
    }
    console.log('[Calendar] visibleDays computed:', days.length, 'days');
    return days;
  }, [currentDate, viewMode]);

  useEffect(() => {
    console.log('[Calendar] useEffect task-deadlines:', { selectedCalendarId, visibleDaysLength: visibleDays.length });
    if (!selectedCalendarId || visibleDays.length === 0) {
      console.log('[Calendar] Skipping task-deadlines fetch - missing data');
      return;
    }
    const start = visibleDays[0].format('YYYY-MM-DD');
    const end = visibleDays[visibleDays.length - 1].format('YYYY-MM-DD');
    console.log('[Calendar] Fetching task-deadlines:', { start, end, user_id: selectedCalendarId });
    CalendarService.getTaskDeadlines({ start_date: start, end_date: end, user_id: selectedCalendarId })
      .then(r => {
        console.log('[Calendar] Task deadlines received:', r.data.length, 'tasks');
        console.log('[Calendar] Sample tasks:', r.data.slice(0, 3));
        setAllDeadlineTasks(r.data);
      })
      .catch(e => {
        console.error('[Calendar] Task deadlines error:', e);
        console.error('[Calendar] Error details:', e.response?.data);
        setAllDeadlineTasks([]);
      });
  }, [visibleDays, selectedCalendarId]);

  const virtualEvents = useMemo(() => {
    console.log('[Calendar] virtualEvents useMemo:', { 
      visibleDaysLength: visibleDays.length, 
      allDeadlineTasksLength: allDeadlineTasks.length,
      allBirthdaysLength: allBirthdays.length 
    });
    console.log('[Calendar] allDeadlineTasks sample:', allDeadlineTasks.slice(0, 3));
    const virt = [];
    const todayDs = dayjs().format('YYYY-MM-DD');
    console.log('[Calendar] Today is:', todayDs);
    console.log('[Calendar] visibleDays range:', visibleDays[0]?.format('YYYY-MM-DD'), 'to', visibleDays[visibleDays.length - 1]?.format('YYYY-MM-DD'));
    
    // Find the first visible day to show overdue tasks if today is not visible
    const firstVisibleDay = visibleDays[0]?.format('YYYY-MM-DD');
    const todayIsVisible = visibleDays.some(d => d.format('YYYY-MM-DD') === todayDs);
    const overdueTargetDay = todayIsVisible ? todayDs : firstVisibleDay;
    console.log('[Calendar] Today visible:', todayIsVisible, 'Overdue target day:', overdueTargetDay);
    
    for (const day of visibleDays) {
      const ds = day.format('YYYY-MM-DD');
      
      const hasBirthday = allBirthdays.some(b => {
        if (!b.birth_date) return false;
        const bDate = dayjs(b.birth_date);
        return bDate.month() === day.month() && bDate.date() === day.date();
      });

      if (hasBirthday) {
        virt.push({
          id: `birthday-${ds}`, title: 'День рождения', event_type: 'birthday',
          start_time: `${ds}T07:00:00`, end_time: `${ds}T07:10:00`,
          _date: ds, _virtual: true, creator_id: selectedCalendarId,
        });
      }

      const dayTasks = allDeadlineTasks.filter(t => t.due_date === ds);
      const assigneeTasks = dayTasks.filter(t => t.user_role === 'assignee');
      const curatorTasks = dayTasks.filter(t => t.user_role === 'curator');
      const validatorTasks = dayTasks.filter(t => t.user_role === 'validator');

      if (assigneeTasks.length > 0) {
        virt.push({
          id: `deadline-assignee-${ds}`, title: `Дедлайны (исполнитель) (${assigneeTasks.length})`, event_type: 'task_deadline',
          start_time: `${ds}T07:20:00`, end_time: `${ds}T07:30:00`,
          _date: ds, _virtual: true, creator_id: selectedCalendarId,
          tasks: assigneeTasks,
        });
      }

      if (curatorTasks.length > 0) {
        virt.push({
          id: `deadline-curator-${ds}`, title: `Дедлайны (куратор) (${curatorTasks.length})`, event_type: 'task_deadline',
          start_time: `${ds}T07:40:00`, end_time: `${ds}T07:50:00`,
          _date: ds, _virtual: true, creator_id: selectedCalendarId,
          tasks: curatorTasks,
        });
      }

      if (validatorTasks.length > 0) {
        virt.push({
          id: `deadline-validator-${ds}`, title: `Дедлайны (постановщик) (${validatorTasks.length})`, event_type: 'task_deadline',
          start_time: `${ds}T07:50:00`, end_time: `${ds}T08:00:00`,
          _date: ds, _virtual: true, creator_id: selectedCalendarId,
          tasks: validatorTasks,
        });
      }
      
      // Overdue tasks on today's cell or first visible day if today is not visible
      console.log('[Calendar] Checking day:', ds, 'is overdue target:', ds === overdueTargetDay);
      if (ds === overdueTargetDay) {
        const overdueTasks = allDeadlineTasks.filter(t => dayjs(t.due_date).isBefore(dayjs(), 'day'));
        console.log('[Calendar] Overdue tasks for', ds, ':', overdueTasks.length);
        if (overdueTasks.length > 0) {
          console.log('[Calendar] Adding overdue event with', overdueTasks.length, 'tasks');
          virt.push({
            id: `deadline-overdue-${ds}`, title: `Просроченные задачи (${overdueTasks.length})`, event_type: 'task_overdue',
            start_time: `${ds}T08:00:00`, end_time: `${ds}T09:00:00`,
            _date: ds, _virtual: true, creator_id: selectedCalendarId,
            tasks: overdueTasks,
          });
        }
      }
    }
    console.log('[Calendar] virtualEvents created:', virt.length, 'events');
    console.log('[Calendar] virtualEvents types:', virt.map(e => e.event_type));
    return virt;
  }, [visibleDays, selectedCalendarId, allDeadlineTasks, allBirthdays]);

  const allEvents = useMemo(() => [...events, ...virtualEvents], [events, virtualEvents]);

  const upcomingEvents = useMemo(() => {
    const now = dayjs();
    return allEvents
      .filter(e => e.start_time && dayjs(e.start_time).isAfter(now))
      .sort((a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf())
      .slice(0, 50);
  }, [allEvents]);

  // ─── duration auto-calc for forms ─────────────────────────

  const makeDurationHandler = (formInstance) => (changed, allValues) => {
    if (changed.duration !== undefined && allValues.start_time) {
      const end = allValues.start_time.add(changed.duration, 'minute');
      formInstance.setFieldValue('end_time', end);
    } else if (changed.start_time && allValues.duration) {
      const end = changed.start_time.add(allValues.duration, 'minute');
      formInstance.setFieldValue('end_time', end);
    } else if (changed.end_time && allValues.start_time) {
      const dur = changed.end_time.diff(allValues.start_time, 'minute');
      if (dur > 0) formInstance.setFieldValue('duration', dur);
    }
  };

  // ─── type picker & creation ───────────────────────────────

  const openTypePicker = (date) => {
    setClickedDate(date || dayjs());
    setTypePickerOpen(true);
  };

  const handleTypePick = (type) => {
    setTypePickerOpen(false);
    const defaultStart = clickedDate ? clickedDate.hour(9).minute(0).second(0) : dayjs().hour(9).minute(0).second(0);
    const defaultEnd = defaultStart.add(1, 'hour');

    if (type === 'event') {
      eventForm.resetFields();
      eventForm.setFieldsValue({ start_time: defaultStart, end_time: defaultEnd, duration: 60 });
      setEventFormOpen(true);
    } else if (type === 'meeting') {
      meetingForm.resetFields();
      meetingForm.setFieldsValue({ start_time: defaultStart, end_time: defaultEnd, duration: 60 });
      setMeetingFormOpen(true);
    } else if (type === 'videoconference') {
      videoForm.resetFields();
      videoForm.setFieldsValue({ start_time: defaultStart, end_time: defaultEnd, duration: 60 });
      setVideoFormOpen(true);
    }
  };

  // Create simple event
  const handleDeleteEvent = async (id) => {
    try {
      await CalendarService.deleteEvent(id);
      message.success('Событие удалено');
      setEventDetailOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (e) {
      message.error('Ошибка удаления события');
    }
  };

  const handleCreateEvent = async (values) => {
    try {
      await CalendarService.createEvent({
        title: values.title,
        description: values.description || null,
        event_type: 'event',
        start_time: values.start_time.second(0).millisecond(0).toISOString(),
        end_time: values.end_time.second(0).millisecond(0).toISOString(),
        location: values.location || null,
        owner_id: selectedCalendarId,
        task_ids: values.task_ids || [],
        project_ids: values.project_ids || [],
      });
      message.success('Событие создано');
      setEventFormOpen(false);
      fetchEvents();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка создания события');
    }
  };

  // Create meeting (совещание) — creates calendar event + meeting
  const handleCreateMeeting = async (values) => {
    try {
      const calRes = await CalendarService.createEvent({
        title: values.title,
        description: values.description || null,
        event_type: 'meeting',
        start_time: values.start_time.second(0).millisecond(0).toISOString(),
        end_time: values.end_time.second(0).millisecond(0).toISOString(),
        location: values.location || null,
        participant_ids: values.participant_ids || [],
        owner_id: selectedCalendarId,
        task_ids: values.task_ids || [],
        project_ids: values.project_ids || [],
      });
      await MeetingsService.createMeeting({
        title: values.title,
        description: values.description || null,
        start_time: values.start_time.second(0).millisecond(0).toISOString(),
        end_time: values.end_time.second(0).millisecond(0).toISOString(),
        location: values.location || null,
        calendar_event_id: calRes.data.id,
        participant_ids: values.participant_ids || [],
        secretary_id: values.secretary_id || null,
        task_ids: values.task_ids || [],
        project_ids: values.project_ids || [],
      });
      message.success('Совещание создано');
      setMeetingFormOpen(false);
      fetchEvents();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка создания совещания');
    }
  };

  // Create videoconference — creates calendar event + meeting with jitsi link
  const handleCreateVideo = async (values) => {
    try {
      const calRes = await CalendarService.createEvent({
        title: values.title,
        description: values.description || null,
        event_type: 'videoconference',
        start_time: values.start_time.second(0).millisecond(0).toISOString(),
        end_time: values.end_time.second(0).millisecond(0).toISOString(),
        participant_ids: values.participant_ids || [],
        owner_id: selectedCalendarId,
      });
      const meetRes = await MeetingsService.createMeeting({
        title: values.title,
        description: values.description || null,
        start_time: values.start_time.second(0).millisecond(0).toISOString(),
        end_time: values.end_time.second(0).millisecond(0).toISOString(),
        calendar_event_id: calRes.data.id,
        participant_ids: values.participant_ids || [],
        external_participants: values.external_participants || [],
      });
      message.success(`Видеоконференция создана. Ссылка: ${meetRes.data.jitsi_url}`);
      setVideoFormOpen(false);
      fetchEvents();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка создания видеоконференции');
    }
  };

  // ─── permissions ──────────────────────────────────────────

  const handleGrantPermission = async (values) => {
    try {
      await CalendarService.createPermission(values);
      message.success('Доступ предоставлен');
      permForm.resetFields();
      fetchPermissions();
      fetchCalendars();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка');
    }
  };

  const handleRevokePermission = async (id) => {
    await CalendarService.deletePermission(id);
    message.success('Доступ отозван');
    fetchPermissions();
    fetchCalendars();
  };

  // ─── event clicks ─────────────────────────────────────────

  const handleSaveDecision = async (entityType, linkId, text, publish = false) => {
    try {
      let res;
      if (entityType === 'task') {
        res = await CalendarService.updateLinkedTaskDecision(selectedEvent.id, linkId, { decision_text: text, is_decision_published: publish });
      } else if (entityType === 'project') {
        res = await CalendarService.updateLinkedProjectDecision(selectedEvent.id, linkId, { decision_text: text, is_decision_published: publish });
      }
      setSelectedEvent(res.data);
      message.success(publish ? 'Решение опубликовано' : 'Черновик сохранён');
      fetchEvents();
    } catch (e) {
      console.error(e);
      message.error(e.response?.data?.detail || 'Ошибка сохранения решения');
    }
  };

  const handleTaskClick = (taskId) => {
    setPreviewTask(null);
    setTaskDetailOpen(true);
    TasksService.getTaskById(taskId).then(r => setPreviewTask(r.data)).catch(() => message.error('Ошибка загрузки задачи'));
  };

  const openDeadlineModal = async (day) => {
    const tasksForDay = allDeadlineTasks.filter(t => t.due_date === day);
    setDeadlineTasks(tasksForDay);
    setDeadlineModalOpen(true);
  };

  const openBirthdayModal = async (day) => {
    try {
      const r = await CalendarService.getBirthdays({ date: day });
      setBirthdayPeople(r.data);
    } catch { setBirthdayPeople([]); }
    setBirthdayModalOpen(true);
  };

  const handleEventClick = (evt) => {
    if (evt._virtual) {
      if (evt.id.startsWith('deadline-')) openDeadlineModal(evt._date);
      else if (evt.id.startsWith('birthday-')) openBirthdayModal(evt._date);
      return;
    }
    // Non-virtual event
    if (evt.event_type === 'meeting' || evt.event_type === 'videoconference') {
      if (evt.meeting_id) {
        navigate(`/meetings/${evt.meeting_id}`);
      }
      return;
    }
    // Regular event — show detail card
    setSelectedEvent(evt);
    setEventDetailOpen(true);
  };

  // ─── cell click (open type picker) ────────────────────────

  const handleCellClick = (date) => {
    if (!canEditSelected) return;
    openTypePicker(date);
  };

  // ─── navigation ───────────────────────────────────────────

  const goToday = () => setCurrentDate(dayjs());
  const goPrev = () => {
    if (viewMode === 'month') setCurrentDate(d => d.subtract(1, 'month'));
    else if (viewMode === 'week') setCurrentDate(d => d.subtract(1, 'week'));
    else setCurrentDate(d => d.subtract(1, 'day'));
  };
  const goNext = () => {
    if (viewMode === 'month') setCurrentDate(d => d.add(1, 'month'));
    else if (viewMode === 'week') setCurrentDate(d => d.add(1, 'week'));
    else setCurrentDate(d => d.add(1, 'day'));
  };

  const headerTitle = useMemo(() => {
    if (viewMode === 'month') return currentDate.format('MMMM YYYY');
    if (viewMode === 'day') return currentDate.format('dddd, D MMMM YYYY');
    const ws = currentDate.startOf('isoWeek');
    const we = currentDate.endOf('isoWeek');
    if (ws.month() === we.month()) return `${ws.format('D')} – ${we.format('D MMMM YYYY')}`;
    return `${ws.format('D MMM')} – ${we.format('D MMM YYYY')}`;
  }, [currentDate, viewMode]);

  // ─── helpers ──────────────────────────────────────────────

  const formatDuration = (start, end) => {
    if (!start || !end) return '—';
    const mins = dayjs(end).diff(dayjs(start), 'minute');
    if (mins < 60) return `${mins} мин`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
  };

  // ─── month cell render ────────────────────────────────────

  const renderTaskTooltip = (tasks) => (
    <div style={{ maxHeight: 200, overflowY: 'auto', paddingRight: 4 }}>
      {tasks.map(t => {
        const isOverdue = dayjs().isAfter(t.due_date, 'day');
        return (
          <div key={t.task_id} style={{ marginBottom: 4 }}>
            <div style={{ fontWeight: 'bold' }}>
              <a onClick={(ev) => { ev.stopPropagation(); handleTaskClick(t.task_id); }} style={{ color: '#000', textDecoration: 'underline', cursor: 'pointer' }}>
                {t.title}
              </a>
            </div>
          </div>
        );
      })}
    </div>
  );

  const dateCellRender = (value) => {
    const dayStr = value.format('YYYY-MM-DD');
    const dayEvents = allEvents.filter(e => dayjs(e.start_time).format('YYYY-MM-DD') === dayStr);
    console.log('[Calendar] dateCellRender for day:', dayStr, 'events count:', dayEvents.length);
    if (dayEvents.length > 0) {
      console.log('[Calendar] dateCellRender events:', dayEvents.map(e => ({ id: e.id, title: e.title, type: e.event_type })));
    }
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {dayEvents.map(e => {
          const color = EVENT_COLORS[e.event_type] || '#1677ff';
          if (e.event_type === 'task_deadline' || e.event_type === 'task_overdue') {
            if (e.tasks) {
              return (
                <li key={e.id} style={{ cursor: 'pointer', marginBottom: 2 }} onClick={(ev) => { ev.stopPropagation(); handleEventClick(e); }}>
                  <Tooltip title={renderTaskTooltip(e.tasks)} color={color}>
                    <Badge color={color} text={<span style={{ fontSize: 11 }}>{e.title}</span>} />
                  </Tooltip>
                </li>
              );
            }
          }
          return (
            <li key={e.id} style={{ cursor: 'pointer', marginBottom: 2 }}
              onClick={(ev) => { ev.stopPropagation(); handleEventClick(e); }}>
              <Badge color={color} text={<span style={{ fontSize: 11 }}>{e.title}</span>} />
            </li>
          );
        })}
      </ul>
    );
  };

  // ─── week / day grid ──────────────────────────────────────

  const weekDays = useMemo(() => {
    const start = currentDate.startOf('isoWeek');
    return Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));
  }, [currentDate]);

  const getEventsForDayHour = (day, hour) => {
    const dayStr = day.format('YYYY-MM-DD');
    return allEvents.filter(e => {
      const s = dayjs(e.start_time);
      return s.format('YYYY-MM-DD') === dayStr && s.hour() === hour;
    });
  };

  const renderTimeGrid = (days) => {
    const colWidth = days.length > 1 ? `${100 / days.length}%` : '100%';
    return (
      <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', borderBottom: '2px solid #f0f0f0', background: '#fafafa' }}>
          <div style={{ width: 60, minWidth: 60, borderRight: '1px solid #f0f0f0' }} />
          {days.map(day => {
            const isToday = day.isSame(dayjs(), 'day');
            return (
              <div key={day.format('YYYY-MM-DD')} style={{ width: colWidth, textAlign: 'center', padding: '8px 0', borderRight: '1px solid #f0f0f0', cursor: 'pointer' }}
                onClick={() => { setCurrentDate(day); setViewMode('day'); }}>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{day.format('dd')}</div>
                <div style={{
                  fontSize: 20, fontWeight: 600, width: 36, height: 36, lineHeight: '36px', margin: '0 auto', borderRadius: '50%',
                  background: isToday ? '#1677ff' : 'transparent', color: isToday ? '#fff' : 'inherit',
                }}>{day.format('D')}</div>
              </div>
            );
          })}
        </div>
        {/* Grid */}
        <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
          {HOURS.map(hour => (
            <div key={hour} style={{ display: 'flex', minHeight: 60, borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ width: 60, minWidth: 60, textAlign: 'right', paddingRight: 8, paddingTop: 2, fontSize: 12, color: '#8c8c8c', borderRight: '1px solid #f0f0f0' }}>
                {String(hour).padStart(2, '0')}:00
              </div>
              {days.map(day => {
                const hourEvts = getEventsForDayHour(day, hour);
                return (
                  <div
                    key={day.format('YYYY-MM-DD')}
                    style={{ width: colWidth, borderRight: '1px solid #f0f0f0', padding: '2px 4px', position: 'relative', cursor: canEditSelected ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (!canEditSelected) return;
                      openTypePicker(day.hour(hour).minute(0).second(0));
                    }}
                  >
                    {hourEvts.map((e, idx) => {
                      const start = dayjs(e.start_time);
                      const end = dayjs(e.end_time);
                      const durationMin = end.diff(start, 'minute');
                      const heightPx = Math.max(durationMin, 28);
                      const topOffset = start.minute();
                      const bgColor = EVENT_COLORS[e.event_type] || EVENT_COLORS.event;
                      const leftOffset = 4 + (idx * 20);
                      return (
                        <Tooltip key={e.id} title={(e.event_type === 'task_deadline' || e.event_type === 'task_overdue') && e.tasks ? renderTaskTooltip(e.tasks) : `${e.title} (${start.format('HH:mm')} – ${end.format('HH:mm')})`} color={(e.event_type === 'task_deadline' || e.event_type === 'task_overdue') ? bgColor : undefined}>
                          <div
                            onClick={(ev) => { ev.stopPropagation(); handleEventClick(e); }}
                            style={{
                              position: 'absolute', top: topOffset, left: leftOffset, right: 4, height: heightPx,
                              background: bgColor, opacity: 0.9, borderRadius: 4, padding: '2px 6px',
                              color: '#fff', fontSize: 12, overflow: 'hidden', whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis', cursor: 'pointer', zIndex: 1 + idx,
                              boxShadow: idx > 0 ? '-2px 0 5px rgba(0,0,0,0.15)' : 'none'
                            }}
                          >{e.title}</div>
                        </Tooltip>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ─── selected calendar info ───────────────────────────────
  const selectedCal = availableCalendars.find(c => c.user_id === selectedCalendarId);
  const canEditSelected = selectedCal?.can_edit ?? false;

  // ─── participant select options ───────────────────────────
  const userOptions = allUsers.map(u => ({ value: u.id, label: u.full_name }));

  // ─── duration form fields (render function, not component) ─
  const renderDurationFields = () => (
    <Space style={{ width: '100%' }} size="middle">
      <Form.Item name="start_time" label="Дата и время начала" rules={[{ required: true, message: 'Укажите начало' }]} style={{ flex: 1 }}>
        <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="duration" label="Длительность (мин)">
        <InputNumber min={5} max={480} step={5} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="end_time" label="Время окончания" rules={[{ required: true, message: 'Укажите конец' }]} style={{ flex: 1 }}>
        <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
      </Form.Item>
    </Space>
  );

  // ─── render ───────────────────────────────────────────────

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Календарь</h1>
        <p className="page-subtitle">События, совещания и дедлайны</p>
      </div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Space wrap>
          {canEditSelected && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openTypePicker(currentDate)}>Новое событие</Button>
          )}
          <Button onClick={goToday}>Сегодня</Button>
          <Button icon={<LeftOutlined />} onClick={goPrev} />
          <Button icon={<RightOutlined />} onClick={goNext} />
          <Text strong style={{ fontSize: 16, textTransform: 'capitalize' }}>{headerTitle}</Text>
        </Space>
        <Space wrap>
          <Select
            style={{ minWidth: 240 }}
            value={selectedCalendarId}
            onChange={(v) => setSelectedCalendarId(v)}
            options={availableCalendars.map(c => ({
              value: c.user_id,
              label: c.is_own ? `${c.full_name} (мой)` : c.full_name,
            }))}
            placeholder="Выберите календарь"
          />
          <Button icon={<ShareAltOutlined />} onClick={() => { setPermModalOpen(true); fetchPermissions(); }}>
            Доступ
          </Button>
          <Button icon={<SyncOutlined />} onClick={() => { setNewCaldavPassword(null); setCaldavModalOpen(true); }}>
            Синхронизация
          </Button>
          <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} buttonStyle="solid">
            <Radio.Button value="month">Месяц</Radio.Button>
            <Radio.Button value="week">Неделя</Radio.Button>
            <Radio.Button value="day">День</Radio.Button>
          </Radio.Group>
        </Space>
      </div>

      {/* Legend */}
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => (
          <Tag key={k} color={EVENT_COLORS[k]}>{v}</Tag>
        ))}
      </div>

      {/* Views */}
      {viewMode === 'month' && (
        <Calendar
          cellRender={dateCellRender}
          value={currentDate}
          onSelect={(date) => { setCurrentDate(date); handleCellClick(date); }}
          headerRender={() => null}
        />
      )}
      {viewMode === 'week' && renderTimeGrid(weekDays)}
      {viewMode === 'day' && renderTimeGrid([currentDate])}

      {/* Upcoming Events List */}
      <Card style={{ marginTop: 24 }} title={<><CalendarOutlined /> Предстоящие 50 событий</>} size="small">
        {upcomingEvents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 16, color: '#8c8c8c' }}>Нет предстоящих событий</div>
        ) : (
          <List
            size="small"
            dataSource={upcomingEvents}
            renderItem={e => {
              const start = dayjs(e.start_time);
              const color = EVENT_COLORS[e.event_type] || EVENT_COLORS.event;
              return (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleEventClick(e)}
                >
                  <List.Item.Meta
                    avatar={<Tag color={color}>{EVENT_TYPE_LABELS[e.event_type] || e.event_type}</Tag>}
                    title={<span style={{ fontWeight: 600 }}>{e.title}</span>}
                    description={
                      <Space>
                        <span>{start.format('DD.MM.YYYY HH:mm')}</span>
                        {e.location && <span>— {e.location}</span>}
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Card>

      {/* ── Step 1: Type picker modal ─── */}
      <Modal
        title="Выберите тип мероприятия"
        open={typePickerOpen}
        onCancel={() => setTypePickerOpen(false)}
        footer={null}
        width={480}
      >
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', padding: '16px 0' }}>
          <Card
            hoverable
            style={{ width: 130, textAlign: 'center' }}
            onClick={() => handleTypePick('meeting')}
          >
            <TeamOutlined style={{ fontSize: 32, color: EVENT_COLORS.meeting }} />
            <div style={{ marginTop: 8, fontWeight: 600 }}>Совещание</div>
          </Card>
          <Card
            hoverable
            style={{ width: 130, textAlign: 'center' }}
            onClick={() => handleTypePick('videoconference')}
          >
            <VideoCameraOutlined style={{ fontSize: 32, color: EVENT_COLORS.videoconference }} />
            <div style={{ marginTop: 8, fontWeight: 600 }}>Видео&shy;конференция</div>
          </Card>
          <Card
            hoverable
            style={{ width: 130, textAlign: 'center' }}
            onClick={() => handleTypePick('event')}
          >
            <CalendarOutlined style={{ fontSize: 32, color: EVENT_COLORS.event }} />
            <div style={{ marginTop: 8, fontWeight: 600 }}>Событие</div>
          </Card>
        </div>
      </Modal>

      {/* ── Step 2a: Event form ─── */}
      <Modal title={editMode ? "Редактировать событие" : "Новое событие"} open={eventFormOpen} onCancel={() => { setEventFormOpen(false); setEditMode(false); }} onOk={() => eventForm.submit()} okText={editMode ? "Сохранить" : "Создать"} width={{ xs: '100%', sm: 640 }} style={{ top: 20 }} forceRender>
        <Form form={eventForm} layout="vertical" onFinish={editMode ? handleUpdateEvent : handleCreateEvent} onValuesChange={makeDurationHandler(eventForm)}>
          <Form.Item name="title" label="Название события" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <SmartMentionsInput rows={3} />
          </Form.Item>
          {renderDurationFields()}
          <Form.Item name="location" label="Место проведения">
            <Input />
          </Form.Item>
          <Form.Item name="project_ids" label="Связанные проекты">
            <Select mode="multiple" placeholder="Выберите проекты" showSearch optionFilterProp="label" options={projects.map(p => ({value: p.id, label: p.name}))} />
          </Form.Item>
          <Form.Item name="task_ids" label="Связанные задачи">
            <Select mode="multiple" placeholder="Выберите задачи" showSearch optionFilterProp="label" options={allTasks.map(t => ({value: t.id, label: t.title}))} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Step 2b: Meeting form ─── */}
      <Modal title={editMode ? "Редактировать совещание" : "Новое совещание"} open={meetingFormOpen} onCancel={() => { setMeetingFormOpen(false); setEditMode(false); }} onOk={() => meetingForm.submit()} okText={editMode ? "Сохранить" : "Создать"} width={{ xs: '100%', sm: 640 }} style={{ top: 20 }} forceRender>
        <Form form={meetingForm} layout="vertical" onFinish={editMode ? handleUpdateEvent : handleCreateMeeting} onValuesChange={makeDurationHandler(meetingForm)}>
          <Form.Item name="title" label="Тема совещания" rules={[{ required: true, message: 'Введите тему' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <SmartMentionsInput rows={3} />
          </Form.Item>
          {renderDurationFields()}
          <Form.Item name="location" label="Место проведения">
            <Input />
          </Form.Item>
          <Form.Item name="participant_ids" label="Участники">
            <Select mode="multiple" placeholder="Выберите участников" showSearch optionFilterProp="label" options={userOptions} />
          </Form.Item>
          <Form.Item name="secretary_id" label="Секретарь">
            <Select placeholder="Выберите секретаря" showSearch optionFilterProp="label" options={userOptions} allowClear />
          </Form.Item>
          <Form.Item name="project_ids" label="Связанные проекты">
            <Select mode="multiple" placeholder="Выберите проекты" showSearch optionFilterProp="label" options={projects.map(p => ({value: p.id, label: p.name}))} />
          </Form.Item>
          <Form.Item name="task_ids" label="Связанные задачи">
            <Select mode="multiple" placeholder="Выберите задачи" showSearch optionFilterProp="label" options={allTasks.map(t => ({value: t.id, label: t.title}))} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Step 2c: Videoconference form ─── */}
      <Modal title={editMode ? "Редактировать ВКС" : "Новая видеоконференция"} open={videoFormOpen} onCancel={() => { setVideoFormOpen(false); setEditMode(false); }} onOk={() => videoForm.submit()} okText={editMode ? "Сохранить" : "Создать"} width={{ xs: '100%', sm: 640 }} style={{ top: 20 }} forceRender>
        <Form form={videoForm} layout="vertical" onFinish={editMode ? handleUpdateEvent : handleCreateVideo} onValuesChange={makeDurationHandler(videoForm)}>
          <Form.Item name="title" label="Тема ВКС" rules={[{ required: true, message: 'Введите тему' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <SmartMentionsInput rows={3} />
          </Form.Item>
          {renderDurationFields()}
          <Form.Item name="participant_ids" label="Участники из списка сотрудников">
            <Select mode="multiple" placeholder="Выберите участников" showSearch optionFilterProp="label" options={userOptions} />
          </Form.Item>
          <Divider orientation="left">Внешние участники</Divider>
          <Form.List name="external_participants">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item {...restField} name={[name, 'name']} rules={[{ required: true, message: 'Введите имя' }]}>
                      <Input placeholder="ФИО" />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'email']} rules={[{ required: true, message: 'Введите email' }]}>
                      <Input placeholder="Email" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Добавить внешнего участника
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* ── Event detail card modal ─── */}
      <Modal
        title={<span><CalendarOutlined /> Карточка события</span>}
        open={eventDetailOpen}
        onCancel={() => { setEventDetailOpen(false); setSelectedEvent(null); }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Button icon={<ShareAltOutlined />} onClick={handleCopyEventLink}>Скопировать ссылку</Button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {(canEditSelected || isAdmin || isSecretary) && selectedEvent && !['task_deadline', 'birthday', 'regular_meeting'].includes(selectedEvent.event_type) && (
                <>
                  <Button icon={<CalendarOutlined />} onClick={handleEditEventClick}>
                    Редактировать
                  </Button>
                  <Button danger icon={<DeleteOutlined />} onClick={() => {
                    Modal.confirm({
                      title: 'Удалить событие?',
                      content: 'Это действие необратимо.',
                      onOk: () => handleDeleteEvent(selectedEvent.id)
                    });
                  }}>
                    Удалить
                  </Button>
                </>
              )}
              <Button onClick={() => { setEventDetailOpen(false); setSelectedEvent(null); }}>Закрыть</Button>
            </div>
          </div>
        }
        width={{ xs: '100%', sm: 560 }}
        style={{ top: 20 }}
      >
        {selectedEvent && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Название">{selectedEvent.title}</Descriptions.Item>
            <Descriptions.Item label="Описание">{selectedEvent.description ? <LinkifyText text={selectedEvent.description} /> : '—'}</Descriptions.Item>
            <Descriptions.Item label="Дата начала">{dayjs(selectedEvent.start_time).format('DD.MM.YYYY')}</Descriptions.Item>
            <Descriptions.Item label="Время начала">{dayjs(selectedEvent.start_time).format('HH:mm')}</Descriptions.Item>
            <Descriptions.Item label="Продолжительность">{formatDuration(selectedEvent.start_time, selectedEvent.end_time)}</Descriptions.Item>
            <Descriptions.Item label="Время окончания">{dayjs(selectedEvent.end_time).format('HH:mm')}</Descriptions.Item>
            <Descriptions.Item label="Место проведения">{selectedEvent.location || '—'}</Descriptions.Item>
          </Descriptions>
        )}
        
        {selectedEventParticipants && selectedEventParticipants.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Divider orientation="left" style={{ margin: '12px 0' }}>Участники</Divider>
            <List
              size="small"
              dataSource={selectedEventParticipants}
              renderItem={p => {
                const user = allUsers.find(u => String(u.id) === String(p.user_id));
                let statusColor = 'default';
                let statusText = 'Ожидает';
                if (p.status === 'accepted') { statusColor = 'green'; statusText = 'Принял'; }
                if (p.status === 'declined') { statusColor = 'red'; statusText = 'Отклонил'; }
                
                return (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} />}
                      title={user?.full_name || `ID ${p.user_id}`}
                    />
                    <Tag color={statusColor}>{statusText}</Tag>
                  </List.Item>
                );
              }}
            />
          </div>
        )}

        {selectedEvent && selectedEvent.linked_projects?.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Divider orientation="left" style={{ margin: '12px 0' }}>Связанные проекты</Divider>
            {selectedEvent.linked_projects.map(p => (
              <div key={p.id} style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 4 }}><Tag color="geekblue">{p.title}</Tag></div>
                {p.is_decision_published ? (
                  <Alert message="Решение опубликовано" description={<LinkifyText text={p.decision_text} />} type="success" showIcon />
                ) : (
                  <div>
                    <Input.TextArea
                      defaultValue={p.decision_text}
                      placeholder={`Решение по проекту "${p.title}"...`}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      onBlur={(e) => handleSaveDecision('project', p.id, e.target.value, false)}
                      id={`project-decision-${p.id}`}
                    />
                    <Button 
                      type="primary" 
                      size="small" 
                      style={{ marginTop: 8 }} 
                      onClick={() => {
                        const val = document.getElementById(`project-decision-${p.id}`).value;
                        handleSaveDecision('project', p.id, val, true);
                      }}
                    >
                      Опубликовать
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {selectedEvent && selectedEvent.linked_tasks?.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Divider orientation="left" style={{ margin: '12px 0' }}>Связанные задачи</Divider>
            {selectedEvent.linked_tasks.map(t => (
              <div key={t.id} style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 4 }}><Tag color="cyan">{t.title}</Tag></div>
                {t.is_decision_published ? (
                  <Alert message="Решение опубликовано" description={<LinkifyText text={t.decision_text} />} type="success" showIcon />
                ) : (
                  <div>
                    <Input.TextArea
                      defaultValue={t.decision_text}
                      placeholder={`Решение по задаче "${t.title}"...`}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      onBlur={(e) => handleSaveDecision('task', t.id, e.target.value, false)}
                      id={`task-decision-${t.id}`}
                    />
                    <Button 
                      type="primary" 
                      size="small" 
                      style={{ marginTop: 8 }} 
                      onClick={() => {
                        const val = document.getElementById(`task-decision-${t.id}`).value;
                        handleSaveDecision('task', t.id, val, true);
                      }}
                    >
                      Опубликовать
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* ── Permissions modal ─── */}
      <Modal title="Управление доступом к календарю" open={permModalOpen} onCancel={() => setPermModalOpen(false)} footer={null} width={600}>
        <Divider orientation="left">Предоставить доступ</Divider>
        <Form form={permForm} layout="inline" onFinish={handleGrantPermission} style={{ marginBottom: 16 }}>
          <Form.Item name="granted_user_id" rules={[{ required: true, message: 'Выберите сотрудника' }]}>
            <Select style={{ width: 260 }} placeholder="Выберите сотрудника" showSearch optionFilterProp="label" options={userOptions} />
          </Form.Item>
          <Form.Item name="can_edit" valuePropName="checked" initialValue={false}>
            <Switch checkedChildren="Редактирование" unCheckedChildren="Просмотр" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Дать доступ</Button>
          </Form.Item>
        </Form>
        <Divider orientation="left">Текущие разрешения</Divider>
        <List
          dataSource={myPermissions}
          locale={{ emptyText: 'Нет выданных разрешений' }}
          renderItem={p => {
            const user = allUsers.find(u => String(u.id) === String(p.granted_user_id));
            return (
              <List.Item actions={[<Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleRevokePermission(p.id)}>Отозвать</Button>]}>
                <List.Item.Meta title={user?.full_name || `ID ${p.granted_user_id}`} description={p.can_edit ? 'Просмотр + редактирование' : 'Только просмотр'} />
              </List.Item>
            );
          }}
        />
      </Modal>

      {/* ── Task deadlines modal ─── */}
      <Modal title={<span><CheckSquareOutlined /> Срок выполнения задачи</span>} open={deadlineModalOpen} onCancel={() => setDeadlineModalOpen(false)} footer={null} width={600}>
        {deadlineTasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#8c8c8c' }}>Нет данных</div>
        ) : (
          <List dataSource={deadlineTasks} renderItem={t => {
            const isOverdue = dayjs().isAfter(t.due_date, 'day');
            return (
              <List.Item>
                <List.Item.Meta
                  title={<a onClick={() => handleTaskClick(t.task_id)} style={{ color: isOverdue ? '#ff4d4f' : undefined, cursor: 'pointer' }}>{t.title}</a>}
                  description={
                    <Space>
                      <Tag color={t.priority === 'CRITICAL' ? 'red' : t.priority === 'HIGH' ? 'orange' : t.priority === 'MEDIUM' ? 'blue' : 'default'}>{t.priority}</Tag>
                      <Tag>{t.status}</Tag>
                      {t.project_name && <Tag color="geekblue">{t.project_name}</Tag>}
                    </Space>
                  }
                />
              </List.Item>
            );
          }} />
        )}
      </Modal>

      {/* ── Birthdays modal ─── */}
      <Modal title={<span><GiftOutlined /> Дни рождения</span>} open={birthdayModalOpen} onCancel={() => setBirthdayModalOpen(false)} footer={null}>
        {birthdayPeople.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#8c8c8c' }}>Нет данных</div>
        ) : (
          <List dataSource={birthdayPeople} renderItem={b => (
            <List.Item>
              <List.Item.Meta avatar={<GiftOutlined style={{ fontSize: 24, color: '#eb2f96' }} />} title={b.full_name} description={b.source === 'employee' ? 'Сотрудник' : 'Контрагент'} />
            </List.Item>
          )} />
        )}
      </Modal>

      {/* ── Task detail modal ─── */}
      <Modal
        title={<span><CheckSquareOutlined /> Карточка задачи</span>}
        open={taskDetailOpen}
        onCancel={() => setTaskDetailOpen(false)}
        footer={[
          <Button key="goto" type="primary" onClick={() => navigate(`/tasks?taskId=${previewTask?.id}`)} disabled={!previewTask}>
            Перейти к задаче
          </Button>,
          <Button key="close" onClick={() => setTaskDetailOpen(false)}>
            Закрыть
          </Button>
        ]}
        width={800}
      >
        {!previewTask ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Загрузка данных задачи...</p>
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <div>
                <h4>Основная информация</h4>
                <p><strong>Описание:</strong> {previewTask.description ? <LinkifyText text={previewTask.description} style={{ display: 'inline' }} /> : 'Нет описания'}</p>
                <p><strong>Тип:</strong> {taskTypeOptions.find(opt => opt.value === previewTask.task_type)?.label || previewTask.task_type}</p>
                <p><strong>Проект:</strong> {projects.find(p => String(p.id) === String(previewTask.project_id))?.name || `ID: ${previewTask.project_id}`}</p>
                <p><strong>Постановщик:</strong> {allUsers.find(u => String(u.id) === String(previewTask.initiator_id))?.full_name || `ID: ${previewTask.initiator_id}`}</p>
                <p><strong>Исполнитель:</strong> {previewTask.assignee_id ? (allUsers.find(u => String(u.id) === String(previewTask.assignee_id))?.full_name || `ID: ${previewTask.assignee_id}`) : 'Не назначен'}</p>
              </div>
              
              <div>
                <h4>Статус и даты</h4>
                <p><strong>Статус:</strong> {statusOptions.find(opt => opt.value === previewTask.status)?.label || previewTask.status}</p>
                <p><strong>Приоритет:</strong> {priorityOptions.find(opt => opt.value === previewTask.priority)?.label || previewTask.priority}</p>
                <p><strong>Прогресс:</strong> {previewTask.progress}%</p>
                <p><strong>Срок выполнения (назначенный):</strong> {previewTask.due_date ? dayjs(previewTask.due_date).format('DD.MM.YYYY') : 'Не указан'}</p>
                <p><strong>Срок выполнения (актуализированный):</strong> {previewTask.actual_due_date ? dayjs(previewTask.actual_due_date).format('DD.MM.YYYY') : 'Не указан'}</p>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div>
                <h4>Метрики</h4>
                <p><strong>Оценка часов:</strong> {previewTask.estimated_hours || 'Не указано'}</p>
                <p><strong>Бюджет:</strong> {previewTask.budget ? `${previewTask.budget} ₽` : 'Не указан'}</p>
              </div>
              
              <div>
                <h4>Системная информация</h4>
                <p><strong>Создана:</strong> {dayjs(previewTask.created_at).format('DD.MM.YYYY HH:mm')}</p>
                <p><strong>Обновлена:</strong> {dayjs(previewTask.updated_at).format('DD.MM.YYYY HH:mm')}</p>
                <p><strong>Завершена:</strong> {previewTask.completed_at ? dayjs(previewTask.completed_at).format('DD.MM.YYYY HH:mm') : 'Нет'}</p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── CalDAV sync modal ─── */}
      <Modal title={<span><SyncOutlined /> Синхронизация с телефоном (CalDAV/iCal)</span>} open={caldavModalOpen} onCancel={() => setCaldavModalOpen(false)} footer={null} width={600}>
        <Typography.Paragraph>
          Вы можете настроить синхронизацию своего календаря с мобильным устройством (iOS, Android, macOS).
        </Typography.Paragraph>
        <Alert
          message="Двусторонняя синхронизация (DAVx⁵ — Android)"
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          description={
            <CaldavDavx5Block onPassword={(pw) => setNewCaldavPassword(pw)} newCaldavPassword={newCaldavPassword} />
          }
        />
        <Alert
          message="Адрес для подписки (iCal)"
          description={
            <div>
              <Text copyable style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, display: 'inline-block', wordBreak: 'break-all' }}>
                {`${window.location.protocol}//${window.location.host}/api/v1/caldav/${selectedCalendarId || 1}/events.ics`}
              </Text>
              <div style={{ marginTop: 8, fontSize: '0.85em', color: 'gray' }}>
                Для режима "Только чтение" (iOS: Настройки -&gt; Календарь -&gt; Учетные записи -&gt; Добавить -&gt; Другое -&gt; Подписной календарь).
              </div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Alert
          message="Адрес сервера CalDAV (Двусторонняя связь)"
          description={
            <div>
              <Text copyable style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, display: 'inline-block', wordBreak: 'break-all' }}>
                {`${window.location.protocol}//${window.location.host}/api/v1/caldav/${selectedCalendarId || 1}/`}
              </Text>
              <div style={{ marginTop: 8, fontSize: '0.85em', color: 'gray' }}>
                В разработке: Поддержка авторизации и создания событий прямо с устройства. Введите этот адрес в настройках CalDAV вашего устройства.
              </div>
            </div>
          }
          type="success"
          showIcon
        />
      </Modal>
    </div>
  );
}
